import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  CurrentSeason,
  ExplorerScope,
  ExplorerSource,
  MetricGroup,
  PlayerFilterOptionsResponse,
  SeamInfo,
} from "../models/Explorer";
import { GlossaryResponse } from "../models/Glossary";
import { Team } from "../models/Team";
import { Auth } from "aws-amplify";
import { IdReport } from "../models/IdReport";
import { GameWithID } from "../models/Game";
import { ExplorerEnvironment } from "../data/Cache";
import { NBAGameListItem } from "./types";
import type { PickerGame, PickerPlayer } from "../components/Elements/GamePicker/GamePicker";
import { TeamReportProps } from "../TeamReport/types";
import { transformTeamReportApiResponse } from "../TeamReport/utils";
import {
  ShotQueryContext,
  ShotDetailResponse,
} from "../TeamReport/ShotDetailModal/types";
import {
  RosterPlayer,
  RotationPlan,
} from "../components/Pages/Coaching/Rotations/types";
import {
  HalftimeDashboardRow,
  HalftimeApiResponse,
} from "../components/Pages/Coaching/HalftimeDashboard/types";
import { transformHalftimeApiResponse } from "../components/Pages/Coaching/HalftimeDashboard/utils";
import { ActionDetailResponse } from "../TeamReport/ActionDetailModal/types";
import { VideoPlaylistResponse } from "../TeamReport/types";
import {
  AddEmbeddedQueryRequest,
  CreateResearchArticleRequest,
  PublishResearchArticleRequest,
  ReorderEmbeddedQueriesRequest,
  ResearchArticleDetail,
  ResearchArticleVersionDetail,
  ResearchArticleVersionsResponse,
  ResearchArticlesIndexResponse,
  ResearchCategoriesResponse,
  ResearchEmbeddedQueriesResponse,
  ResearchEmbeddedQueryRef,
  SearchableSavedQueriesResponse,
  UpdateEmbeddedQueryRequest,
  UpdateResearchArticleRequest,
} from "../models/Research";
import {
  PlayerStatsGarden,
  PlayerStatsPlayerResponse,
  PlayerStatsPopulationsResponse,
  PlayerStatsScopesResponse,
  PlayerStatsTableResponse,
} from "../models/PlayerStats";
import {
  BoardGame,
  DeleteUploadResponse,
  PlayerSetting,
  ReboundingDashboard,
  ReopenedUpload,
  RevertUploadResponse,
  UploadConfirmRequest,
  UploadConfirmResponse,
  UploadPreview,
  UploadRecord,
} from "../components/Pages/Rebounding/types";

type ReportType = "offense" | "defense";

/** The scope a page's requests carry: derived from the page, never from a stored query. */
export function scopeForEnvironment(
  environment: ExplorerEnvironment,
): ExplorerScope {
  return environment === ExplorerEnvironment.DRAFT ? "draft" : "nba";
}

/**
 * What a lazy chart / video call forwards so it reads the query's provider
 * (F1 contract §1). No `scope`: a lazy call runs on the page that shows the
 * result, so the page's scope is the query's.
 */
export interface LazyCallContext {
  source?: ExplorerSource | null;
}

type ContextFilters = Array<{ name: string; op: string; values: any[] }>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Element 0 of an exec_query array is the query context (ENGINE_NOTES §a): the
 * backend reads `scope` from there, not from the body.
 */
export function withQueryScope(
  query: MetricGroup[],
  scope: ExplorerScope,
): MetricGroup[] {
  if (query.length === 0) return query;
  return [{ ...query[0], scope }, ...query.slice(1)];
}

/**
 * Both scopes are served by one API (`REACT_APP_EXPLORER_API`); a request says
 * which one it belongs to. The rule, shared with the backend (E5 item 9), lives
 * in the four verb methods below so no endpoint can forget it:
 *  - GET / DELETE → `?scope=`
 *  - POST → top-level `"scope"` in the JSON body
 *  - PUT → both (the backend contract names GET and POST only; archive / claim
 *    have no other body, so either reading works)
 *  - exec_query → also in element 0 (`withQueryScope`)
 * The page's scope always wins over a `scope` a caller or a stored query supplied.
 */
export class ExplorerAPI {
  private baseUrl: string;
  readonly scope: ExplorerScope;

  constructor(environment: ExplorerEnvironment = ExplorerEnvironment.NBA) {
    const baseUrl = process.env.REACT_APP_EXPLORER_API;
    if (!baseUrl) {
      throw new Error(
        "Missing environment variable REACT_APP_EXPLORER_API for Explorer API base URL",
      );
    }
    // Several local .env values end in "/", which doubled the separator.
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.scope = scopeForEnvironment(environment);
  }

  // A few legacy callers pass endpoints with a leading "/".
  private url(endpoint: string): string {
    return `${this.baseUrl}/${endpoint.replace(/^\/+/, "")}`;
  }

  private scopedConfig(config?: AxiosRequestConfig): AxiosRequestConfig {
    return { ...config, params: { ...config?.params, scope: this.scope } };
  }

  private scopedBody(data: any): any {
    if (data === undefined || data === null) return { scope: this.scope };
    return isPlainObject(data) ? { ...data, scope: this.scope } : data;
  }

  public async get(endpoint: string, config?: AxiosRequestConfig) {
    return axios.get(this.url(endpoint), this.scopedConfig(config));
  }

  public async post(endpoint: string, data: any, config?: AxiosRequestConfig) {
    // A non-object body cannot carry the key, so it falls back to the query string.
    const scoped = isPlainObject(data) || data == null;
    return axios.post(
      this.url(endpoint),
      this.scopedBody(data),
      scoped ? config : this.scopedConfig(config),
    );
  }

  public async put(endpoint: string, data?: any, config?: AxiosRequestConfig) {
    return axios.put(
      this.url(endpoint),
      this.scopedBody(data),
      this.scopedConfig(config),
    );
  }

  public async delete(endpoint: string, config?: AxiosRequestConfig) {
    return axios.delete(this.url(endpoint), this.scopedConfig(config));
  }

  async getFilters() {
    return this.get(`explorer/api/get_filters`);
  }

  async getMetrics() {
    return this.get(`explorer/api/get_metrics`);
  }

  /** Derived filter/metric glossary. Includes hidden entries with their status. */
  async getGlossary(): Promise<AxiosResponse<GlossaryResponse>> {
    return this.get(`explorer/api/glossary`);
  }

  /** Seam titles / summaries for a `seams_crossed` key (E5). */
  /** The scope's current season from the league schedule (F1 contract §7a). */
  async getCurrentSeason(): Promise<AxiosResponse<CurrentSeason>> {
    return this.get(`api/current_season`);
  }

  async getSeams(): Promise<AxiosResponse<{ seams: SeamInfo[] }>> {
    return this.get(`explorer/api/seams`);
  }

  async getPlayerFilterOptions(
    params: {
      seasons?: string;
      leagues?: string;
    },
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<PlayerFilterOptionsResponse>> {
    const queryParams = new URLSearchParams();
    if (params.seasons) queryParams.set("seasons", params.seasons);
    if (params.leagues) queryParams.set("leagues", params.leagues);

    const queryString = queryParams.toString();
    return this.get(
      `explorer/api/get_player_filter_options${queryString ? "?" + queryString : ""}`,
      config,
    );
  }

  async executeQuery(
    query: MetricGroup[],
    accessType: string,
    queryId?: string,
  ) {
    const q = {
      query: withQueryScope(query, this.scope),
      accessType: accessType,
    };
    if (queryId) {
      return this.post(`explorer/api/exec_query/${queryId}`, q);
    }
    return this.post(`explorer/api/exec_query`, q);
  }

  async loadQueries() {
    return this.get(`explorer/api/load_queries`);
  }

  async loadQuery(queryId: string) {
    return this.get(`explorer/api/load_queries/${queryId}`);
  }

  async saveQuery(query: SavedQueryRequest) {
    return this.post(`explorer/api/save_query`, {
      ...query,
      ...(query.query ? { query: withQueryScope(query.query, this.scope) } : {}),
    });
  }

  async editQuery(queryId: string, query: SavedQueryRequest) {
    return this.put(`explorer/api/update_query/${queryId}`, query);
  }

  async archiveQuery(queryId: string) {
    return this.put(`explorer/api/archive_query/${queryId}`);
  }

  async claimQuery(queryId: string) {
    return this.put(`explorer/api/claim_query/${queryId}`);
  }

  async registerQuery(
    query: MetricGroup[],
  ): Promise<AxiosResponse<{ query_id: string }>> {
    // Scoped in element 0 as exec_query is, so the registered hash is the one
    // the Explorer computes when it runs the link.
    return this.post("explorer/api/register_query", {
      query: withQueryScope(query, this.scope),
    });
  }

  // ---- Player stats ----
  // Roll-up blobs served as tables. Endpoints documented in
  // nba_explorer/app/api/player_stats/CLAUDE.md. `garden` picks the population
  // (NBA blobs vs prospect blobs) and is independent of which API host we're on.
  async getPlayerStatsScopes(
    garden: PlayerStatsGarden,
    signal?: AbortSignal,
  ): Promise<AxiosResponse<PlayerStatsScopesResponse>> {
    return this.get(`api/player_stats/scopes?garden=${garden}`, { signal });
  }

  /** One section's columns, not the whole table — see the endpoint docstring. An
   *  unknown or omitted section falls back to the first one server-side, so callers
   *  don't have to validate before asking. */
  async getPlayerStatsTable(
    garden: PlayerStatsGarden,
    league: string,
    season: number,
    section?: string | null,
    cols?: string[],
    signal?: AbortSignal,
  ): Promise<AxiosResponse<PlayerStatsTableResponse>> {
    return this.get(
      `api/player_stats/table?garden=${garden}` +
        `&league=${encodeURIComponent(league)}&season=${season}` +
        (section ? `&section=${encodeURIComponent(section)}` : "") +
        (cols?.length ? `&cols=${encodeURIComponent(cols.join(","))}` : ""),
      { signal },
    );
  }

  /** `scopes` as "<league>:<season>" pairs. Values only — see `get_populations`. */
  async getPlayerStatsPopulations(
    garden: PlayerStatsGarden,
    scopes: string[],
    section?: string | null,
    cols?: string[],
    signal?: AbortSignal,
  ): Promise<AxiosResponse<PlayerStatsPopulationsResponse>> {
    return this.get(
      `api/player_stats/populations?garden=${garden}` +
        `&scopes=${encodeURIComponent(scopes.join(","))}` +
        (section ? `&section=${encodeURIComponent(section)}` : "") +
        (cols?.length ? `&cols=${encodeURIComponent(cols.join(","))}` : ""),
      { signal },
    );
  }

  async getPlayerStatsPlayer(
    garden: PlayerStatsGarden,
    playerId: string,
    signal?: AbortSignal,
  ): Promise<AxiosResponse<PlayerStatsPlayerResponse>> {
    return this.get(
      `api/player_stats/player/${encodeURIComponent(playerId)}?garden=${garden}`,
      { signal },
    );
  }

  // ---- Research wiki ----
  // Endpoints documented in nba_explorer/app/api/research/CLAUDE.md.
  // Visibility filtering happens server-side based on Cognito group claims.
  async getResearchCategories(): Promise<
    AxiosResponse<ResearchCategoriesResponse>
  > {
    return this.get("api/research/categories");
  }

  async getResearchArticlesIndex(): Promise<
    AxiosResponse<ResearchArticlesIndexResponse>
  > {
    return this.get("api/research/articles/index");
  }

  async getResearchArticle(
    slug: string,
  ): Promise<AxiosResponse<ResearchArticleDetail>> {
    return this.get(`api/research/articles/${slug}`);
  }

  // Editor mutations (Phase 2). PUT/publish/unpublish return 409 with a
  // `current_version` field when the caller's `expected_version` is stale —
  // callers should catch axios errors and inspect `err.response.status`.
  async createResearchArticle(
    body: CreateResearchArticleRequest,
  ): Promise<AxiosResponse<ResearchArticleDetail>> {
    return this.post("api/research/articles", body);
  }

  async updateResearchArticle(
    slug: string,
    body: UpdateResearchArticleRequest,
  ): Promise<AxiosResponse<ResearchArticleDetail>> {
    return this.put(`api/research/articles/${slug}`, body);
  }

  async deleteResearchArticle(
    slug: string,
  ): Promise<AxiosResponse<{ ok: boolean; slug: string }>> {
    return this.delete(`api/research/articles/${slug}`);
  }

  async publishResearchArticle(
    slug: string,
    body: PublishResearchArticleRequest = {},
  ): Promise<AxiosResponse<ResearchArticleDetail>> {
    return this.post(`api/research/articles/${slug}/publish`, body);
  }

  async unpublishResearchArticle(
    slug: string,
    body: PublishResearchArticleRequest = {},
  ): Promise<AxiosResponse<ResearchArticleDetail>> {
    return this.post(`api/research/articles/${slug}/unpublish`, body);
  }

  async getResearchArticleVersions(
    slug: string,
  ): Promise<AxiosResponse<ResearchArticleVersionsResponse>> {
    return this.get(`api/research/articles/${slug}/versions`);
  }

  async getResearchArticleVersion(
    slug: string,
    versionNumber: number,
  ): Promise<AxiosResponse<ResearchArticleVersionDetail>> {
    return this.get(`api/research/articles/${slug}/versions/${versionNumber}`);
  }

  /**
   * Phase 3 read path — composite endpoint that returns each embedded
   * query with its full saved-query body + table_state, ready to be
   * executed via `executeQuery` and rendered in `ExplorerDataGrid`.
   */
  async getResearchEmbeddedQueries(
    slug: string,
  ): Promise<AxiosResponse<ResearchEmbeddedQueriesResponse>> {
    return this.get(`api/research/articles/${slug}/embedded_queries`);
  }

  // ---- Phase 3 editor (admin) mutations ----
  // Embed CRUD doesn't bump the article's `current_version` — the
  // backend treats embeds as siblings independent of the article body.

  async getSearchableSavedQueries(): Promise<
    AxiosResponse<SearchableSavedQueriesResponse>
  > {
    return this.get(`api/research/saved_queries/searchable`);
  }

  async addResearchEmbeddedQuery(
    slug: string,
    body: AddEmbeddedQueryRequest,
  ): Promise<AxiosResponse<ResearchEmbeddedQueryRef>> {
    return this.post(`api/research/articles/${slug}/queries`, body);
  }

  async updateResearchEmbeddedQuery(
    slug: string,
    embedId: string,
    body: UpdateEmbeddedQueryRequest,
  ): Promise<AxiosResponse<ResearchEmbeddedQueryRef>> {
    return this.put(`api/research/articles/${slug}/queries/${embedId}`, body);
  }

  async reorderResearchEmbeddedQueries(
    slug: string,
    body: ReorderEmbeddedQueriesRequest,
  ): Promise<AxiosResponse<{ ok: boolean }>> {
    return this.put(`api/research/articles/${slug}/queries/order`, body);
  }

  async deleteResearchEmbeddedQuery(
    slug: string,
    embedId: string,
  ): Promise<AxiosResponse<{ ok: boolean; id: string }>> {
    return this.delete(`api/research/articles/${slug}/queries/${embedId}`);
  }

  // ---- Phase 4 related-article endpoints (admin) ----
  // Mutations don't bump `current_version`, mirroring embed CRUD.

  async addResearchRelatedArticle(
    slug: string,
    relatedSlug: string,
  ): Promise<AxiosResponse<{ ok: boolean; slug: string; related_slug: string }>> {
    return this.post(
      `api/research/articles/${slug}/related/${relatedSlug}`,
      {},
    );
  }

  async removeResearchRelatedArticle(
    slug: string,
    relatedSlug: string,
  ): Promise<AxiosResponse<{ ok: boolean; slug: string; related_slug: string }>> {
    return this.delete(
      `api/research/articles/${slug}/related/${relatedSlug}`,
    );
  }

  async getTeamReportList(team: Team): Promise<TeamReportListResponse> {
    return this.get(`api/team_report/list/${team.abbrev}`);
  }

  async getTeamReport(
    id: string,
    team: Team,
    season?: number,
  ): Promise<TeamReportProps> {
    return this.get(`api/team_report/${id}`).then((response) =>
      transformTeamReportApiResponse(
        response.data,
        [],
        `${team.city} ${team.name}`,
        team.name,
        team.abbrev,
        season,
      ),
    );
  }

  async getGameLog(limit?: number): Promise<GameLogResponse> {
    return this.get(`api/get_games/POR`, { params: { limit: limit } });
  }

  async getUpcomingGames() {
    return this.get(`api/get_upcoming_games`);
  }

  async getTeamGames(
    teamId: number,
    season: number,
  ): Promise<NBAGameListItem[]> {
    return this.get(`api/teams/${teamId}/seasons/${season}/games`).then(
      ({ data }) => data,
    );
  }

  /**
   * `source` asks for an overlap season's other provider — a dev comparison
   * report the backend serves only with TEAM_REPORT_DEV_SOURCE set (R1).
   */
  async getTeamSeasonReport(
    team: Team,
    season: number,
    reportType: "season" | "playoff_season" | "preseason_season" | "L5" | "L10" | "L20" = "season",
    source?: ExplorerSource | null,
  ): Promise<TeamReportProps> {
    return this.get(
      `api/teams/${team.nbaId}/reports/seasons/${season}/${reportType}`,
      source ? { params: { source } } : undefined,
    ).then(({ data }) =>
      transformTeamReportApiResponse(
        data.report,
        data.availableGameIds,
        `${team.city} ${team.name}`,
        team.name,
        team.abbrev,
        season,
        data.last_saved,
      ),
    );
  }

  async getTeamGameReport(
    team: Team,
    gameId: string,
    source?: ExplorerSource | null,
  ): Promise<TeamReportProps> {
    return this.get(
      `api/teams/${team.nbaId}/reports/games/${gameId}`,
      source ? { params: { source } } : undefined,
    ).then(
      ({ data }) =>
        transformTeamReportApiResponse(
          data.report,
          data.availableGameIds,
          `${team.city} ${team.name}`,
          team.name,
          team.abbrev,
          undefined,
          data.last_saved,
        ),
    );
  }

  /** A team's finished regular-season games of a season, newest first with scores: the custom pickers' list. */
  async getCustomReportGames(
    team: Team,
    season: number,
  ): Promise<{ games: PickerGame[]; players: PickerPlayer[] }> {
    return this.get(`api/teams/${team.nbaId}/reports/custom/games`, {
      params: { season },
    }).then(({ data }) => ({ games: data.games ?? [], players: data.players ?? [] }));
  }

  /**
   * Queues the team's custom window report over `games` (core game ids); built on the cron box, read by key. `peek`
   * only asks where that pick stands ("none": never requested) and queues nothing.
   */
  async requestCustomReport(
    team: Team,
    season: number,
    games: string[],
    peek = false,
  ): Promise<CustomReportRequest> {
    return this.post(`api/teams/${team.nbaId}/reports/custom`, {
      season,
      games,
      ...(peek ? { peek: true } : {}),
    }).then(({ data }) => data);
  }

  /** A custom window report by key: the report once built, else its request's status. */
  async getCustomReport(team: Team, season: number, key: string): Promise<CustomReportStatus> {
    return this.get(`api/teams/${team.nbaId}/reports/custom/${key}`, {
      params: { season },
    }).then(({ data }) =>
      data.status === "ready"
        ? {
            ...data,
            report: transformTeamReportApiResponse(
              data.report,
              data.availableGameIds,
              `${team.city} ${team.name}`,
              team.name,
              team.abbrev,
              season,
              data.last_saved,
            ),
          }
        : data,
    );
  }

  /**
   * Fetch live halftime dashboard data from Redis cache.
   * Used by the halftime dashboard for in-game stats.
   */
  async getHalftimeDashboardData(
    gameId: string,
  ): Promise<HalftimeDashboardRow[]> {
    return this.get(`api/halftime/${gameId}`).then(
      ({ data }: { data: HalftimeApiResponse }) =>
        transformHalftimeApiResponse(data),
    );
  }

  /**
   * Fetch detailed shot data for a specific report row context.
   * Used by the shot detail modal when clicking on shot distribution bars.
   * The report's `source` (stamped on the row context) goes top level so a
   * dev `?source=` comparison report drills into the provider it was built from.
   */
  async getShotDetail(
    teamNbaId: string,
    season: string | undefined,
    queryContext: ShotQueryContext,
    seasonType?: string,
  ): Promise<ShotDetailResponse> {
    return this.post(`api/team_report/shot_detail`, {
      teamNbaId,
      ...(season ? { season } : {}),
      ...(seasonType ? { seasonType } : {}),
      ...(queryContext.source ? { source: queryContext.source } : {}),
      queryContext,
    }).then(({ data }) => data);
  }

  async getTeamRoster(
    teamId: number,
    leagueId: string,
  ): Promise<{ players: RosterPlayer[] }> {
    return this.get(`api/leagues/${leagueId}/teams/${teamId}/roster`).then(
      ({ data }) => data,
    );
  }

  async getRotationPlan(gameId: string): Promise<RotationPlan> {
    return this.get(`api/coaching/rotation-plans/${gameId}`).then(
      ({ data }) => data,
    );
  }

  async saveRotationPlan(
    gameId: string,
    plan: {
      opponentTeamId: number;
      slotAssignments: Record<string, number | null>;
      excludedPlayerIds: number[];
    },
  ): Promise<void> {
    return this.put(`api/coaching/rotation-plans/${gameId}`, plan).then(
      () => undefined,
    );
  }

  // Rebounding dashboard (RD1): practice sheets + game crash rates. Confirm is the one verified write.
  async getReboundingDashboard(season?: number): Promise<ReboundingDashboard> {
    return this.get(`api/rebounding/dashboard`, season ? { params: { season } } : undefined).then(
      ({ data }) => data,
    );
  }


  async previewReboundingUpload(csv: string, filename: string): Promise<UploadPreview> {
    return this.post(`api/rebounding/upload/preview`, { csv, filename }).then(({ data }) => data);
  }

  async confirmReboundingUpload(body: UploadConfirmRequest): Promise<UploadConfirmResponse> {
    return this.post(`api/rebounding/upload/confirm`, body).then(({ data }) => data);
  }

  // With a game's session: that game's sheet (charting its box-outs).
  async getReboundingBlankSheet(sheet: "crashing" | "boxouts", session?: string): Promise<UploadPreview> {
    return this.get(`api/rebounding/upload/blank`, { params: session ? { sheet, session } : { sheet } }).then(
      ({ data }) => data,
    );
  }

  async getReboundingUploads(): Promise<{ uploads: UploadRecord[]; games: BoardGame[] }> {
    return this.get(`api/rebounding/uploads`).then(({ data }) => data);
  }

  // An edited game's crashing back to tracking's numbers; imports refresh it again (game_rows.py).
  async revertReboundingUpload(uploadId: string): Promise<RevertUploadResponse> {
    return this.post(`api/rebounding/uploads/${uploadId}/revert`, {}).then(({ data }) => data);
  }

  async reopenReboundingUpload(uploadId: string): Promise<ReopenedUpload> {
    return this.get(`api/rebounding/uploads/${uploadId}/review`).then(({ data }) => data);
  }

  async deleteReboundingUpload(uploadId: string): Promise<DeleteUploadResponse> {
    return this.delete(`api/rebounding/uploads/${uploadId}`).then(({ data }) => data);
  }

  async getReboundingPlayers(): Promise<{ players: PlayerSetting[] }> {
    return this.get(`api/rebounding/players`).then(({ data }) => data);
  }

  async setReboundingPlayerHidden(playerId: string, hidden: boolean): Promise<PlayerSetting> {
    return this.put(`api/rebounding/players/${playerId}`, { hidden }).then(({ data }) => data);
  }

  /**
   * Fetch detailed action data for a specific report row context.
   * Used by the action detail modal when clicking on shot distribution bars.
   *
   * Note: The action_detail endpoint expects a different queryContext shape than shot_detail.
   * We extract only the fields the endpoint needs (perspective, filters) and send
   * teamNbaId as an integer (per the endpoint spec).
   */
  async getActionDetail(
    teamNbaId: string,
    season: string | undefined,
    queryContext: ShotQueryContext,
    seasonType?: string,
  ): Promise<ActionDetailResponse> {
    return this.post(`api/team_report/action_detail`, {
      teamNbaId: parseInt(teamNbaId, 10),
      ...(season ? { season } : {}),
      ...(seasonType ? { seasonType } : {}),
      ...(queryContext.source ? { source: queryContext.source } : {}),
      queryContext: {
        perspective: queryContext.perspective,
        filters: queryContext.filters,
      },
    }).then(({ data }) => data);
  }

  /**
   * Generate a CTG video playlist for a report row context.
   * Returns a URL to open in a new tab. NBA only.
   */
  async getVideoPlaylist(
    teamNbaId: string,
    season: string | undefined,
    queryContext: ShotQueryContext,
  ): Promise<VideoPlaylistResponse> {
    return this.post(`api/video/playlist`, {
      teamNbaId: parseInt(teamNbaId, 10),
      ...(season ? { season } : {}),
      ...(queryContext.source ? { source: queryContext.source } : {}),
      perspective: queryContext.perspective,
      filters: queryContext.filters,
    }).then(({ data }) => data);
  }

  /**
   * Generate a CTG video playlist from explicit chance IDs.
   * Used from within the action detail modal where chance IDs are already known.
   * Core chance ids (`ctg:<id>`) are mapped to their SS twins server-side.
   */
  async getVideoPlaylistByChanceIds(
    chanceIds: string[],
  ): Promise<VideoPlaylistResponse> {
    return this.post(`api/video/playlist`, {
      chanceIds,
    }).then(({ data }) => data);
  }

  /*
   * The three Explorer lazy calls below take a result cell's filters merged with
   * the query's global filters, plus the query's `source` (cell context first,
   * `queryContext` fallback — FilterContextCell resolves that). Without the
   * source an explicit-source query's chart and video would read the season
   * default (F1 contract §1a). `scope` is always the page's.
   */

  /**
   * Generate a CTG video playlist from a combined filter array.
   * `video/playlist` reads scope / source top level.
   */
  async getVideoPlaylistByFilters(
    filters: ContextFilters,
    context: LazyCallContext = {},
  ): Promise<VideoPlaylistResponse> {
    return this.post(`api/video/playlist`, {
      filters,
      source: context.source ?? null,
    }).then(({ data }) => data);
  }

  /**
   * Fetch shot detail data from a combined filter array.
   * Used by Explorer DataGrid for lazy-loaded ShotDetailChart cells.
   */
  async getShotDetailByFilters(
    filters: ContextFilters,
    context: LazyCallContext = {},
  ): Promise<ShotDetailResponse> {
    return this.post(`api/team_report/shot_detail`, {
      queryContext: {
        filters,
        scope: this.scope,
        source: context.source ?? null,
      },
    }).then(({ data }) => data);
  }

  /**
   * Fetch action detail data from a combined filter array.
   * Used by Explorer DataGrid for lazy-loaded ActionDetailChart cells.
   */
  async getActionDetailByFilters(
    filters: ContextFilters,
    context: LazyCallContext = {},
  ): Promise<ActionDetailResponse> {
    return this.post(`api/team_report/action_detail`, {
      queryContext: {
        filters,
        scope: this.scope,
        source: context.source ?? null,
      },
    }).then(({ data }) => data);
  }
}

// Set up axios interceptor for auth
axios.interceptors.request.use(async (config) => {
  try {
    const token = (await Auth.currentSession()).getIdToken();
    return Object.assign(config, {
      headers: {
        JwtToken: token.getJwtToken(),
        userId: token.payload.sub,
      },
    });
  } catch {
    // No Cognito session (storybook print harness, local tooling): send the
    // request unauthenticated instead of failing it outright — the deployed
    // backend still rejects wherever auth is required.
    return config;
  }
});

// Legacy functions that use the NBA environment
export function getLegacyTeamReport(
  reportType: ReportType,
  team: Team,
  environment: ExplorerEnvironment = ExplorerEnvironment.NBA,
) {
  const api = new ExplorerAPI(environment);
  const endpoint =
    reportType === "offense"
      ? `api/team_off_report/${team.abbrev}/off`
      : `api/team_def_report/${team.abbrev}/def`;

  return api.get(endpoint);
}

export function getGameReport(gameId: string) {
  const api = new ExplorerAPI(ExplorerEnvironment.NBA);
  return api.get(`api/game_report/${gameId}`);
}

export function getIdReport(reportId: string): Promise<IdReportResponse> {
  const api = new ExplorerAPI(ExplorerEnvironment.NBA);
  return api.get(`api/id_report/${reportId}`);
}

export function getIdReportList() {
  const api = new ExplorerAPI(ExplorerEnvironment.NBA);
  return api.get("/api/id_report/list");
}

export interface TeamChallenges {
  stands: number;
  overturned: number;
  support: number;
}

export type GameChallenges = Record<string, TeamChallenges>;

export interface ChallengesResponse extends AxiosResponse<GameChallenges> {}
export function getGameChallenges(gameId: string): Promise<ChallengesResponse> {
  const api = new ExplorerAPI(ExplorerEnvironment.NBA);
  return api.get(`/api/games/${gameId}/challenges`);
}

/** A season's or a window's challenges: the team's, and its opponents' together (the ownership season view). */
export interface TeamChallengeTotals {
  team: TeamChallenges;
  opponents: TeamChallenges;
  games: number;
  missing: number;
}
export function getTeamChallenges(
  teamNbaId: string,
  abbrev: string,
  season: string,
  seasonType = "regular",
  games?: string[],
): Promise<AxiosResponse<TeamChallengeTotals>> {
  const api = new ExplorerAPI(ExplorerEnvironment.NBA);
  return api.get(`/api/teams/${teamNbaId}/challenges`, {
    params: { abbrev, season, seasonType, ...(games?.length ? { games: games.join(",") } : {}) },
  });
}

// Request interfaces
export interface SavedQueryRequest {
  name: string;
  description: string;
  saveType: string;
  query?: MetricGroup[];
}

// Response interfaces
interface GameLogResponse extends AxiosResponse<GameWithID[]> {}
interface IdReportResponse extends AxiosResponse<IdReport> {}

export interface TeamReportListItem {
  season: number;
  id: string;
  /** A season ahead of the schedule's current one: the roster-preview placeholder (TR3) */
  preview?: boolean;
}

export interface GameReportListItem extends TeamReportListItem {
  gameInfo: {
    gameId: string;
    day: number;
    month: number;
    year: number;
    season: number;
    seasonType: string;
    isHome: boolean;
    opponent: {
      id: string;
      name: string;
      abbrev: string;
    };
    date: string;
  };
}

export interface TeamReportList {
  season: TeamReportListItem[];
  playoff_season: TeamReportListItem[];
  /** Preseason season reports (a 2026-27 test run, TR3) */
  preseason_season?: TeamReportListItem[];
  /** Window reports: each team's Last 5 / 10 / 20 games, ranked against the other teams' (phase 2, 2026-10-27) */
  L5?: TeamReportListItem[];
  L10: TeamReportListItem[];
  L20: TeamReportListItem[];
  game: GameReportListItem[];
}

interface TeamReportListResponse extends AxiosResponse<TeamReportList> {}

/** A custom window report request's state: queued, being built on the cron box, built, or failed. */
export type CustomReportState = "requested" | "building" | "ready" | "failed";

export interface CustomReportRequest {
  key: string;
  /** "none": a peek at a pick never requested */
  status: CustomReportState | "none";
  games: string[];
  since?: string;
}

export interface CustomReportStatus {
  key: string;
  status: CustomReportState;
  since?: string;
  detail?: string | null;
  games?: string[];
  report?: TeamReportProps;
}

// API response structure - matches what the backend actually returns
export interface TeamReportApiResponse {
  overview: any;
  hc_offense: any;
  hc_defense: any;
  dreb_trans_o: any;
  oreb_trans_d: any;
  header: any;
  players: any[];
}

export interface TeamReportResponse extends AxiosResponse<TeamReportApiResponse> {}
